import { useCallback, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { shouldShowUpgrade } from '@/access/access';
import { useAccess } from '@/access/useAccess';
import { useAuth } from '@/auth/AuthProvider';
import { initialsOf } from '@/auth/mapUser';
import { resolveBillingState } from '@/billing/billingState';
import { usePaywall } from '@/billing/PaywallContext';
import { usePaywallPrice } from '@/billing/usePaywallPrice';
import { useT } from '@/i18n/LocaleProvider';
import { useBookmarkCount } from '@/library/useBookmarks';
import { Logo } from '@/components/ui/Logo';
import { cx } from '@/lib/cx';
import { AvatarMenu } from './AvatarMenu';
import styles from './Header.module.css';

/** Шапка 56px: логотип, Catalog · Saved (счётчик) · Canvases, чип «Payment issue» (past_due), Upgrade, аватар с меню. */
export function Header() {
  const { user } = useAuth();
  const access = useAccess();
  const t = useT();
  const { pathname } = useLocation();
  const { openPaywall } = usePaywall();
  const bookmarkCount = useBookmarkCount();
  const price = usePaywallPrice();
  const [menuOpen, setMenuOpen] = useState(false);
  const avatarRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);

  const isCanvases = pathname.startsWith('/canvases');
  const isBilling = /^\/billing(\/|$)/.test(pathname);
  // На самой странице биллинга сигнал о неудавшемся списании уже есть — чип там не нужен.
  const showPaymentIssue = !!user && resolveBillingState(user).paymentIssue && !isBilling;
  const navClass = ({ isActive }: { isActive: boolean }) =>
    cx(styles.navLink, isActive && styles.navActive);

  return (
    <header className={styles.header} data-menu-open={menuOpen || undefined}>
      <div className={styles.inner}>
        <Link to="/" className={styles.brand} aria-label={t('nav.brandCatalog')}>
          <Logo className={styles.brandLogo} />
        </Link>

        <nav className={styles.nav} aria-label={t('nav.primary')}>
          <NavLink to="/" end className={navClass}>
            {t('nav.catalog')}
          </NavLink>
          <NavLink to="/saved" className={navClass} data-tour="nav-saved">
            {t('nav.saved')}
            {bookmarkCount > 0 ? <span className={styles.counter}>{bookmarkCount}</span> : null}
          </NavLink>
          <NavLink
            to="/canvases"
            className={() => cx(styles.navLink, isCanvases && styles.navActive)}
          >
            {t('nav.canvases')}
          </NavLink>
        </nav>

        <div className={styles.spacer} />

        {showPaymentIssue ? (
          <Link
            to="/billing"
            className={styles.paymentIssue}
            aria-label={t('nav.paymentIssueAria')}
          >
            <span className={styles.paymentIssueMark} aria-hidden="true">
              !
            </span>
            <span className={styles.paymentIssueText}>{t('nav.paymentIssue')}</span>
          </Link>
        ) : null}

        {shouldShowUpgrade(access, user, pathname) ? (
          <button
            type="button"
            className={styles.upgrade}
            onClick={() => openPaywall({ trigger: 'intent', source: 'upgrade_button' })}
          >
            {t('nav.upgrade')}
            {/* Цена плана окна по умолчанию — только на десктопе: в 360px шапке места нет. */}
            {price ? (
              <>
                {' '}
                <span className={styles.upgradePrice}>{price}</span>
              </>
            ) : null}
          </button>
        ) : null}

        <div className={styles.avatarWrap}>
          <button
            ref={avatarRef}
            type="button"
            className={styles.avatar}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t('nav.accountMenu')}
            onClick={() => setMenuOpen((v) => !v)}
          >
            {user ? initialsOf(user.name) : ''}
          </button>
          <AvatarMenu open={menuOpen} onClose={closeMenu} anchorRef={avatarRef} />
        </div>
      </div>
    </header>
  );
}
